import React from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Folder, Identifiable } from "@planzone/dto";
import { useMoveDocument } from "@planzone/view-models";

import { DropBox } from "@common/dragAndDrop";
import { FolderIcon } from "../../folderIcon";
import { Props } from "./types";
import { ArrowIcon } from "./arrowIcon";
import styles from "./styles.scss";

function inPath(path: Identifiable<Folder>[], folder: Identifiable<Folder>) {
  return path.find(({ uuid }) => uuid === folder.uuid);
}

function DirectoryStructure({
  planzone,
  folder,
  selectedFolderId,
  selectedFolderPath,
  isRoot,
}: Props) {
  const childFolders = folder.children.filter(
    ({ type }) => type === "folder",
  ) as Identifiable<Folder>[];
  const [isOpen, setIsOpen] = React.useState(isRoot);
  const { t } = useTranslation();
  const push = useNavigate();
  const isSelected = selectedFolderId === folder.uuid;
  const isInPath = inPath(selectedFolderPath, folder);
  const { moveDocument } = useMoveDocument();

  return (
    <div
      className={classNames(styles.directoryStructure, {
        [styles.root]: isRoot,
        [styles.open]: isOpen,
        [styles.selected]: isSelected,
      })}
    >
      <DropBox
        acceptedTypes={["MOVE_DOCUMENT"]}
        drop={(data) => {
          moveDocument({
            document: data.payload,
            from: data.payload.parentUuid,
            to: folder.uuid,
          });
        }}
      >
        {(data, canDrop) => (
          <div
            className={classNames(styles.header, {
              [styles.dropZone]: canDrop,
              [styles.open]: isOpen,
              [styles.selected]: isSelected,
            })}
          >
            <div
              className={styles.toggleIconBox}
              onClick={() => setIsOpen(!isOpen)}
            >
              <ArrowIcon className={styles.toggleIcon} />
            </div>
            <FolderIcon className={styles.icon} />
            <h4
              className={styles.name}
              onClick={() => push(`/documents/folders/${folder.uuid}`)}
            >
              {isRoot
                ? t("documentsPage.sideBar.folder")
                : folder.name === "template"
                  ? "**" +
                    t("documentsPage.documentsPage.templateFolder") +
                    "**"
                  : folder.name}
            </h4>
          </div>
        )}
      </DropBox>
      {(isOpen || isSelected || isInPath) && (
        <div className={styles.children}>
          {childFolders.map((child) => (
            <div key={child.name} className={styles.child}>
              <DirectoryStructure
                planzone={planzone}
                folder={child}
                selectedFolderId={selectedFolderId}
                selectedFolderPath={selectedFolderPath}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { DirectoryStructure };
